import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Video, Radio, Monitor, Mic, Camera, Settings, Shield, Sparkles } from 'lucide-react';
import StudioBuilder from './StudioBuilder';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.05 });
  const [showStudioBuilder, setShowStudioBuilder] = useState(false);

  const services = [
    {
      icon: Video,
      title: 'Studio Builds',
      description: 'We help you design and build out your home or office studio with professional equipment selection, lighting setup, and tech configuration tailored to your space and budget.',
      features: ['Equipment Selection', 'Lighting Design', 'Tech Setup', 'Training + Documentation', 'Studio Design Layout'],
    },
    {
      icon: Radio,
      title: 'Ongoing Broadcast Partner',
      description: 'We take your camera feed and run it through our system, so you get a top-notch, broadcast-quality experience that looks and feels professional, no matter where you are.',
      features: ['Technical Director', 'Recurring Live Stream Management', 'Lower Thirds + Branding Graphics', 'Post-Event Edits'],
    },
    {
      icon: Monitor,
      title: 'Live Production',
      description: 'Professional multi-camera live production for your events, services, and broadcasts with real-time graphics and seamless streaming.',
      features: ['On-Site or Remote Technical Director', 'Multi-Platform Streaming', 'Real-Time Switching', 'Branding Graphics', 'Live Chat Moderation'],
    },
    {
      icon: Mic,
      title: 'Video Production',
      description: 'High-quality recorded content including teaching series, courses, podcasts, promotional videos, and curriculum with professional editing.',
      features: ['Multi-Camera Production', 'Professional Lighting', 'Producer/Operator', 'Post-Production'],
    },
    {
      icon: Camera,
      title: 'Virtual Events',
      description: 'Transform your virtual gatherings into engaging, professional experiences with custom branding and interactive elements.',
      features: ['Custom Branding', 'Audience Engagement', 'Technical Support'],
    },
    {
      icon: Settings,
      title: 'Post Production',
      description: 'Professional editing, color grading, audio mixing, and final delivery to elevate your content.',
      features: ['Video Editing', 'Color Grading', 'Audio Mixing'],
    }
  ];

  return (
    <section id="services" className="relative py-16 sm:py-20 md:py-24 lg:py-32 bg-obsidian overflow-hidden" data-testid="services-section" ref={ref}>
      {/* Animated Floating Neon Glow Orbs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Large orange orb - slow float */}
        <motion.div
          className="absolute w-48 sm:w-64 md:w-80 lg:w-96 h-48 sm:h-64 md:h-80 lg:h-96 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255, 140, 0, 0.3) 0%, rgba(255, 140, 0, 0) 70%)',
            filter: 'blur(60px)',
            top: '10%',
            left: '-10%',
          }}
          animate={{
            x: [0, 100, 50, 0],
            y: [0, 50, 100, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Large amber orb - slow float opposite direction */}
        <motion.div
          className="absolute w-40 sm:w-56 md:w-72 lg:w-80 h-40 sm:h-56 md:h-72 lg:h-80 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(251, 191, 36, 0.3) 0%, rgba(251, 191, 36, 0) 70%)',
            filter: 'blur(60px)',
            top: '30%',
            right: '-5%',
          }}
          animate={{
            x: [0, -80, -40, 0],
            y: [0, 80, 40, 0],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Medium orange orb - faster movement */}
        <motion.div
          className="absolute w-32 sm:w-48 md:w-56 lg:w-64 h-32 sm:h-48 md:h-56 lg:h-64 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255, 140, 0, 0.25) 0%, rgba(255, 140, 0, 0) 70%)',
            filter: 'blur(50px)',
            bottom: '20%',
            right: '20%',
          }}
          animate={{
            x: [0, -60, 30, 0],
            y: [0, -40, 60, 0],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        
        {/* Small amber orb - center area */}
        <motion.div
          className="absolute w-24 sm:w-36 md:w-40 lg:w-48 h-24 sm:h-36 md:h-40 lg:h-48 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(251, 191, 36, 0.2) 0%, rgba(251, 191, 36, 0) 70%)',
            filter: 'blur(40px)',
            bottom: '10%',
            left: '30%',
          }}
          animate={{
            x: [0, 50, -30, 0],
            y: [0, -60, 30, 0],
          }}
          transition={{
            duration: 18,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </div>

      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 mb-10 sm:mb-12 md:mb-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto"
        >
          <span className="font-mono text-sm sm:text-base uppercase tracking-widest text-white mb-3 sm:mb-4 block">
            Our Services
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight mb-4 sm:mb-6" data-testid="services-heading">
            Your <span className="text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}>Message</span>
            <br />
            <span className="text-white">Stewarded <span className="text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}>Well</span></span>
          </h2>
        </motion.div>
      </div>

      {/* Services Grid - 2 rows of 3 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="service-card p-4 sm:p-5 md:p-6 border border-amber-400/30 hover:border-amber-400/60 transition-all flex flex-col"
              data-testid={`service-card-${index}`}
            >
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 border border-amber-400 mb-4 sm:mb-5 md:mb-6">
                <service.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <h3 className="font-heading text-base sm:text-lg font-bold uppercase mb-2 sm:mb-3 text-amber-400">
                {service.title}
              </h3>
              <p className="text-sm sm:text-base text-white mb-3 sm:mb-4 leading-relaxed">
                {service.description}
              </p>
              <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-6">
                {service.features.map((feature, fIndex) => (
                  <span 
                    key={fIndex}
                    className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 border border-white/30 text-white font-bold"
                  >
                    {feature}
                  </span>
                ))}
              </div>
              <a
                href={`https://kingdomvibestudios.hbportal.co/public/69a9c1506b994e0034214eb6?project_type=${encodeURIComponent(service.title)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-auto w-full text-center py-2.5 sm:py-3 font-heading font-bold uppercase tracking-wider text-xs sm:text-sm border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-obsidian transition-all duration-300"
                data-testid={`service-book-${index}`}
              >
                Book
              </a>
            </motion.div>
          ))}
        </div>

        {/* Studio Builder CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-10 sm:mt-12 p-6 sm:p-8 text-center border border-amber-400/20 bg-gradient-to-r from-amber-400/5 via-transparent to-amber-400/5 relative overflow-hidden"
        >
          <div className="relative z-10">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span className="font-mono text-xs sm:text-sm uppercase tracking-widest text-amber-400">AI-Powered</span>
              <Sparkles className="w-5 h-5 text-amber-400" />
            </div>
            <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold uppercase text-white mb-3">
              Design Your <span className="text-amber-400" style={{ textShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}>Dream Studio</span>
            </h3>
            <p className="text-sm sm:text-base text-white/60 max-w-xl mx-auto mb-6">
              Use our AI Studio Builder to visualize your ideal production setup. Select your style, level, and type and see it come to life in seconds.
            </p>
            <button
              onClick={() => setShowStudioBuilder(true)}
              className="btn-neon btn-neon-gold inline-flex items-center gap-2 text-sm sm:text-base px-6 sm:px-8 py-3 sm:py-4"
              data-testid="studio-builder-open-btn"
            >
              <Sparkles size={18} />
              <span>Build Your Studio Concept</span>
            </button>
          </div>
        </motion.div>

        {/* Production Confidence Guarantee - Blue theme */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-10 sm:mt-12 md:mt-16 p-4 sm:p-6 md:p-8 bg-white/5 border border-neon-blue/30 relative"
        >
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
            <div className="flex-shrink-0 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-neon-blue/20 flex items-center justify-center">
              <Shield className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 text-neon-blue" />
            </div>
            <div>
              <h3 className="font-heading text-lg sm:text-xl md:text-2xl font-bold uppercase text-neon-blue mb-2 sm:mb-3" style={{ textShadow: '0 0 20px rgba(0, 240, 255, 0.3)' }}>
                Production Confidence Guarantee
              </h3>
              <p className="text-sm sm:text-base text-white/70 leading-relaxed">
                If technical issues on our end interrupt your production, we'll provide corrective support or post-production solutions at no additional cost. Your message deserves excellence and we stand behind our systems.
              </p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Studio Builder Modal */}
      <StudioBuilder isOpen={showStudioBuilder} onClose={() => setShowStudioBuilder(false)} />
    </section>
  );
};

export default Services;
